/* eslint-disable @typescript-eslint/ban-types */
import {
  JsonFormsCore,
  JsonSchema,
  Layout,
  RankedTester,
  UISchemaElement,
} from "@jsonforms/core";
import { ReactNode } from "react";

type PageElements = {
  type: "Page";
  options: {
    path: string;
    src?: string;
    schema: JsonSchema;
    uischema: Layout;
    data?: any;
    authenticated?: boolean;
    translations?: object;
  };
};

export interface Pages {
  version?: string;
  type: "Pages";
  elements: PageElements[];
  options?: {
    [key: string]: any;
  };
  config?: {
    [key: string]: any;
  };
}

export interface SinglePageProps {
  version?: string;
  config?: {
    [key: string]: any;
  };
  options?: {
    [key: string]: any;
  };
  includeAdvancedRenderers?: boolean;
}

export interface APITemplateProps {
  version?: string;
  endpoint: string;
  templateId: string;
  templatePath?: string;
  templateDataKey?: string;
  isMultiPage?: boolean;
  headerTemplates?: {
    header?: string;
    authHeader?: string;
  };
  footerTemplates?: {
    footer?: string;
    authFooter?: string;
  };
}

export interface FormProps {
  schema: JsonSchema;
  uischema: UISchemaElement;
  data?: any;
  config?: any;
  onChange?: (state: Pick<JsonFormsCore, "data" | "errors">) => void;
  renderers?: {
    tester: RankedTester;
    renderer: any;
  }[];
  authenticated?: boolean;
  /**
   * Whether to include advanced renderers like Rich Text, Multi File Upload or not
   */
  includeAdvancedRenderers?: boolean;
}

export interface FormProviderProps {
  initialValues: any;
  children: ReactNode;
}

export interface Container {
  style?: {
    [key: string]: any;
  };
  wrapperStyle?: {
    [key: string]: any;
  };
}

export interface ModalProps {
  schema: JsonSchema;
  uischema: UISchemaElement;
  data?: any;
  options?: {
    [key: string]: any;
  };
}

export interface DynamicSectionProps {
  uischema: UISchemaElement;
  schema: JsonSchema;
  path: string;
}

export interface ButtonGroupProps {
  uischema: UISchemaElement[];
}

export interface LifecycleHooks {
  onPageLoad?: () => void;
  onSubmitSuccess?: (response: any) => void;
  onSubmitError?: (error: any) => void;
  onSuccessCallbacks?: {};
  onGNavLoad?: () => void;
  onIMSReady?: () => void;
  externalHandlers?: {
    onSubmit?: (data: any, clearForm: () => void) => void;
    onShowModal?: (props: any) => void;
    onCloseModal?: (props: any) => void;
  };
  onDynamicSectionFailure?: (response: any) => void;
}

export type GNavProps = {
  jslib: string;
  loggedInExperience?: string;
  fedsConfig: {
    content: {
      experience: string;
    };
  };
};
export interface CommonJsonHeaders {
  imsData?: object;
  gNav?: GNavProps;
  environment?: object;
  apiHeaders?: object;
  translations?: object;
}
export interface PageProps {
  /**
   * Input JSON
   */
  pages: Pages | SinglePageProps | APITemplateProps;
  storybook?: boolean;
  renderers?: {
    tester: RankedTester;
    renderer: any;
  }[];
  lifecycleHooks?: LifecycleHooks;
  commonJsonHeaders?: CommonJsonHeaders;
  dynamicData?: DynamicData;
  includeAdvancedRenderers?: boolean;
}

export enum PageType {
  SINGLE_PAGE,
  API_TEMPLATE,
  MULTIPLE_PAGE,
}

export interface DynamicFormProps extends PageProps {
  type?: PageType;
  currentEnvironment?: string;
  defaultRoute?: string;
  /**
   * Current locale for the purpose of translation
   */
  locale?: string;
  /**
   * If the translation is missing for a particular language, then it will be shown in the default language
   * if the translation for the default language exists
   */
  defaultLocale?: string;
  // Initial configs
  config?: any;
}

export interface CSSElement {
  url: string;
  integrity: string;
  crossorigin: string;
}
export interface JSElement {
  url: string;
  integrity: string;
  crossorigin: string;
}
export interface ExternalProps {
  css: CSSElement[];
  js: JSElement[];
}

export interface DynamicData {
  data?: object;
}
